import {
  HStack,
  Heading,
  IconButton,
  Stack,
  StackProps,
  Text,
  VStack
} from "@chakra-ui/react"
import useCopyToClipboard from "@pathwright/utils/src/clipboard/useCopyToClipboard"
import UserAvatar from "@pathwright/ux/src/components/avatar/UserAvatar"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import Tooltip from "@pathwright/ux/src/components/tooltip/Tooltip"
import { ContextFragment } from "@pathwright/graphql-client/src/generated"
import MaxLastSeen from "../user/MaxLastSeen"
import SchoolMembershipRoleBadge from "./SchoolMembershipRoleBadge"

const EMAIL_TRUNCATE_CHARS = 28

const Email = ({ email }: { email: string }) => {
  const copyToClipboard = useCopyToClipboard()
  const truncated =
    email.length > EMAIL_TRUNCATE_CHARS
      ? `${email.slice(0, EMAIL_TRUNCATE_CHARS)}…`
      : email

  return (
    <HStack gap={1} minW={0} maxW="100%">
      <Pathicon
        icon="envelope"
        size=".85em"
        color="colorPalette.400"
        flexShrink={0}
      />
      <Tooltip
        fitContent
        popover={{
          openDelay: 200,
          closeDelay: 200,
          positioning: { placement: "bottom-start", gutter: 8 }
        }}
        title={
          <HStack gap={2}>
            <Text as="span" m={0} color="white" whiteSpace="nowrap">
              {email}
            </Text>
            <IconButton
              aria-label="Copy email"
              size="xs"
              variant="ghost"
              colorPalette="whiteAlpha"
              borderRadius="full"
              h="6"
              minW="6"
              flexShrink={0}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                copyToClipboard(email)
              }}
            >
              <Pathicon icon="copy" size=".85em" />
            </IconButton>
          </HStack>
        }
      >
        <Text
          as="span"
          color="colorPalette.400"
          truncate
          minW={0}
          cursor="default"
        >
          {truncated}
        </Text>
      </Tooltip>
    </HStack>
  )
}

type ProfileUserCardProps = {
  user: NonNullable<ContextFragment["me"]>
} & StackProps

function ProfileUserCard({ user, ...stackProps }: ProfileUserCardProps) {
  return (
    <Stack
      as="header"
      className="ProfileUserCard"
      direction={["column", "row"]}
      align={["center", "flex-start"]}
      p={4}
      bg="white"
      borderRadius="augmentationPanel"
      w="100%"
      pos="relative"
      {...stackProps}
    >
      <UserAvatar user={user} size="2xl" />
      <VStack align={["center", "flex-start"]} gap={[4, 2]} w="100%">
        <Heading
          as="h1"
          className="theme-heading theme-heading-bold"
          fontSize="lg"
          m={0}
        >
          {user.full_name}
        </Heading>

        <Stack
          direction={["column", "row"]}
          gap={[1, 2]}
          align={["flex-start", "center"]}
          wrap="wrap"
          w="100%"
          colorPalette="gray"
        >
          {!!user.profile?.bio && (
            <Text m={0} flexBasis="100%" color="colorPalette.400">
              {user.profile.bio}
            </Text>
          )}
          {(user.membership?.role || 0) > 5 && (
            <SchoolMembershipRoleBadge role={user.membership?.role!} />
          )}
          {/* MaxLastSeen is currentlly written in JS (not TSX) and props
            are inferred as required. */}
          {/* @ts-expect-error: */}
          <MaxLastSeen user={user} />
          <Email email={user.email!} />
        </Stack>
      </VStack>
    </Stack>
  )
}

export default ProfileUserCard
